import { z } from "zod/v4";

import {
  AgenticLogicContextDescriptions,
  AgenticLogicInvocationDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import {
  type AgenticLogicIngressRequest,
  AgenticLogicRequestSchema,
} from "./request";
import { AgenticLogicResultItemSchema } from "./result";

// ---------------------------------------------------------------------------
// Invocation shape (what the runner hands the rule)
// ---------------------------------------------------------------------------

/**
 * Platform ceiling for how many slots one invocation may ask a rule to fill.
 *
 * `context.slotsNeeded` is a platform-supplied FACT, not caller input, so the
 * ceiling belongs on the invocation contract — the thing the rule actually
 * sees — rather than only on the two HTTP bodies that happened to carry a
 * `.max(50)` literal each. Every legitimate internal ask is
 * `settings.maxItems` (1-10) plus the onsite rotation reserve, so this is a
 * no-op for real traffic and closes the hole for everything else.
 */
export const AGENTIC_LOGIC_MAX_SLOTS_NEEDED = 50;

export const AgenticLogicContextSchema = z
  .object({
    isPreview: z.boolean().describe(AgenticLogicContextDescriptions.isPreview),
    slotsNeeded: z
      .number()
      .int()
      // NOT `.positive()`: zero is a legitimate ask. A placement can resolve to
      // no slots — post-purchase `maxProductLimit` has no lower bound — and an
      // invocation that carried a zero used to die as a ZodError at the Lambda
      // boundary before the rule body ran. A rule answers zero slots with an
      // empty envelope; the starter's `slotsNeeded <= 0` guard is what makes
      // that happen, and it is load-bearing rather than dead because of this.
      //
      // The ceiling is the missing half of the same reasoning: zero is a real
      // ask, and so is "no ask is unbounded". Bounding it HERE, not only on the
      // serve/evaluate bodies, is what makes the ceiling true for every caller
      // rather than for the two that happen to arrive over HTTP.
      .min(0)
      .max(AGENTIC_LOGIC_MAX_SLOTS_NEEDED)
      .describe(AgenticLogicContextDescriptions.slotsNeeded),
  })
  .describe(AgenticLogicContextDescriptions._schema);
export type AgenticLogicContext = z.infer<typeof AgenticLogicContextSchema>;

export const AgenticLogicInvocationSchema = z
  .object({
    request: AgenticLogicRequestSchema.describe(
      AgenticLogicInvocationDescriptions.request
    ),
    candidates: z
      .array(AgenticLogicResultItemSchema)
      .optional()
      .describe(AgenticLogicInvocationDescriptions.candidates),
    context: AgenticLogicContextSchema.describe(
      AgenticLogicInvocationDescriptions.context
    ),
    secrets: z
      .record(z.string(), z.string())
      .describe(AgenticLogicInvocationDescriptions.secrets),
  })
  .describe(AgenticLogicInvocationDescriptions._schema);
export type AgenticLogicInvocation = z.infer<
  typeof AgenticLogicInvocationSchema
>;

/**
 * An invocation as a CALLER assembles it, before the platform stamps the clock
 * and resolves how many slots the rule is being asked for.
 *
 * Type-only, deliberately: this never crosses a wire — the evaluate and serve
 * bodies are validated as request bodies, and by the time an invocation is
 * parsed anywhere (`AgenticLogicRuleLambdaInputSchema`, the template runner
 * input) both stamps are already on it. A parallel Zod schema would be a second
 * description block and a second thing to keep in step for no validation it
 * performs.
 *
 * The broker and the publish gates each take this and return an
 * {@link AgenticLogicInvocation}; that promotion is the only place `evaluatedAt`
 * and `slotsNeeded` are set, which is what makes "the platform owns now" and
 * "the rule owns how many" checkable by the compiler rather than by convention.
 *
 * `slotsNeeded` left the ingress shape when the item cap became a RULE setting:
 * a caller cannot state it before the rule is resolved, and a caller that
 * could would be the second owner of a number the rule already declares.
 */
export type AgenticLogicIngressInvocation = Omit<
  AgenticLogicInvocation,
  "request" | "context"
> & {
  request: AgenticLogicIngressRequest;
  context: Omit<AgenticLogicContext, "slotsNeeded">;
};

assertDescriptions<
  typeof AgenticLogicContextDescriptions,
  AgenticLogicContext
>();
assertDescriptions<
  typeof AgenticLogicInvocationDescriptions,
  AgenticLogicInvocation
>();
